import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { PointsTimeFilter } from '@/lib/timeFilter';
import type { OwnershipFilter, StatView } from './FilterSheet';
import { getPositionTriggerLabel } from './PositionPriorityRow';

const TIME_LABELS: Record<PointsTimeFilter, string> = {
  '7d': 'Last 7D',
  '14d': 'Last 14D',
  '30d': 'Last 30D',
  season: 'Season',
};

const OWNERSHIP_LABELS: Record<OwnershipFilter, string> = {
  all: 'All ownership',
  available: 'Available',
  taken: 'Taken',
};

interface ActiveFilterChipsProps {
  posFilter: string;
  ownershipFilter: OwnershipFilter;
  pointsTimeFilter: PointsTimeFilter;
  statView: StatView;
  onResetPos: () => void;
  onResetOwnership: () => void;
  onResetTime: () => void;
  onResetStatView: () => void;
  onClearAll: () => void;
}

function Chip({ label, onDismiss }: { label: string; onDismiss: () => void }) {
  return (
    <Badge
      variant="outline"
      className="border-primary/35 bg-primary/10 text-primary pr-1 pl-2 h-6 gap-1 shrink-0 font-medium"
    >
      <span className="text-[11px]">{label}</span>
      <button
        type="button"
        aria-label={`Remove ${label} filter`}
        onClick={onDismiss}
        className="rounded-full hover:bg-primary/15 p-0.5 -mr-0.5"
      >
        <X className="w-3 h-3" />
      </button>
    </Badge>
  );
}

export function ActiveFilterChips({
  posFilter,
  ownershipFilter,
  pointsTimeFilter,
  statView,
  onResetPos,
  onResetOwnership,
  onResetTime,
  onResetStatView,
  onClearAll,
}: ActiveFilterChipsProps) {
  const hasPos = posFilter !== 'All';
  const hasOwnership = ownershipFilter !== 'available';
  const hasTime = pointsTimeFilter !== 'season';
  const hasStatView = statView !== 'season';
  const any = hasPos || hasOwnership || hasTime || hasStatView;

  if (!any) return null;

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden px-4 py-1.5">
      {hasPos && (
        <Chip label={getPositionTriggerLabel(posFilter)} onDismiss={onResetPos} />
      )}
      {hasOwnership && (
        <Chip label={OWNERSHIP_LABELS[ownershipFilter]} onDismiss={onResetOwnership} />
      )}
      {hasTime && (
        <Chip label={TIME_LABELS[pointsTimeFilter]} onDismiss={onResetTime} />
      )}
      {hasStatView && (
        <Chip label="Per Game" onDismiss={onResetStatView} />
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={onClearAll}
        className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground shrink-0"
      >
        Clear all
      </Button>
    </div>
  );
}

export default ActiveFilterChips;
